// Employee Profile — "Employee Coaching History" card (modular, reusable table-based section).
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { EmployeeCoachingHistoryRow } from '../types';
import ProfileDataTable, { ProfileTableColumn } from './ProfileDataTable';

interface EmployeeCoachingHistoryCardProps {
  rows: EmployeeCoachingHistoryRow[];
}

const StatusPill: React.FC<{ status: EmployeeCoachingHistoryRow['status'] }> = ({ status }) => {
  const isOpen = status === 'Open';
  return (
    <View style={[styles.pill, { backgroundColor: isOpen ? AdminColors.yellow50 : AdminColors.green75 }]}>
      <Text style={[styles.pillText, { color: isOpen ? AdminColors.textWarning : AdminColors.green600 }]}>{status}</Text>
    </View>
  );
};

const columns: ProfileTableColumn<EmployeeCoachingHistoryRow>[] = [
  { key: 'employeeName', label: 'Employee Name', flex: 1.3, sortable: true },
  { key: 'actionType', label: 'Action Type', flex: 1, sortable: true, filterable: true },
  { key: 'focusArea', label: 'Focus Area', flex: 1.1, sortable: true, filterable: true },
  { key: 'actionPlan', label: 'Action Plan', flex: 2.2 },
  { key: 'managerSubmitted', label: 'Manager Submitted', flex: 1.3, sortable: true },
  { key: 'activityDate', label: 'Activity Date', flex: 1, sortable: true },
  { key: 'source', label: 'Source', flex: 1, filterable: true },
  {
    key: 'status', label: 'Status', flex: 0.9, sortable: true, filterable: true,
    render: row => <StatusPill status={row.status} />,
  },
  { key: 'result', label: 'Result', flex: 1 },
];

const EmployeeCoachingHistoryCard: React.FC<EmployeeCoachingHistoryCardProps> = ({ rows }) => (
  <View style={styles.card}>
    <Text style={styles.title}>Employee Coaching History</Text>
    <ProfileDataTable
      columns={columns}
      rows={rows}
      searchPlaceholder="Search coaching history…"
      searchFields={['employeeName', 'focusArea', 'actionPlan']}
      emptyLabel="No coaching history records found."
    />
  </View>
);

const styles = StyleSheet.create({
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    padding: 20,
    gap: 12,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 15,
    color: AdminColors.textPrimary,
  },
  pill: {
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
  },
  pillText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
  },
});

export default EmployeeCoachingHistoryCard;
